import {
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Collapse,
  IconButton,
  Box,
  Chip,
  Stack,
} from '@mui/material';
import {
  DirectionsBoat,
  Flag,
  Straighten,
  Height,
  Scale,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import { IconArrowsHorizontal } from '@tabler/icons-react';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';
import {
  RegistrationStatus,
  STATUS_DRAFT,
  STATUS_ONLINE,
} from '../../module/yachts/conf/Data.conf';
import NumbersIcon from '@mui/icons-material/Numbers';
import { Fragment, useCallback, useMemo } from 'react';
import MoveToTemplate from '../../pages/templates/MoveTo.template';
import { MoveToConf } from '../../module/attendances/conf/MoveTo.conf';
import Button from '../common/Button.component';
import { API as API_yachts } from '../../module/yachts/api';
import { useSelector } from 'react-redux';
import { saveItem } from '../../utility/Storage.utility';
import yacht from '../../module/yachts';
import YachtDeletePage from '../../pages/yachts/delete/YachtDelete.page';

const CardHeader = styled(Box)(() => ({
  height: '86px',
  background: `linear-gradient(135deg, #1a237e 0%, #0d47a1 50%, #2196f3 100%)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 24px',
  cursor: 'pointer',
}));

const YachtName = styled(Typography)(() => ({
  fontWeight: 700,
  fontSize: '1.25rem',
  background: `linear-gradient(90deg, #bbdefb 0%, #e3f2fd 100%)`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  display: 'inline-block',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
}));

const InfoGrid = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const InfoItem = ({ icon, label, value }) => {
  const Icon = icon;
  return (
    <Grid item xs={6} lg={12} xl={6}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Box
          sx={{
            fontSize: { xs: 20, md: 26 },
            mr: 1,
            color: '#0d47a1',
            display: { xs: 'none', lg: 'block' },
          }}
        >
          <Icon />
        </Box>
        <Typography
          variant='h4'
          sx={{
            fontWeight: 700,
            color: 'text.secondary',
            display: 'block',
            wordBreak: 'break-word',
            whiteSpace: 'normal',
          }}
        >
          {`${label}: `}
          <Typography
            component='span'
            variant='h5'
            sx={{
              fontWeight: 500,
              display: 'inline',
              wordBreak: 'break-word',
            }}
          >
            {value || '-'}
          </Typography>
        </Typography>
      </Box>
    </Grid>
  );
};

const YachtCard = ({
  yachtData,
  yachtID,
  expandedCard,
  setExpandedCard,
  methods,
}) => {
  const navigate = useNavigate();
  const userAuth = useSelector((state) => state.login?.user);
  const hasPermissions =
    userAuth.permissions[yacht?.tag?.toUpperCase()]?.DELETE === 'true';

  const color = useMemo(
    () =>
      _.find(
        RegistrationStatus,
        (item) =>
          item.label.toUpperCase() ===
          yachtData?.registration_status?.toUpperCase()
      ),
    [yachtData]
  );
  const status = useMemo(
    () => _.get(yachtData, 'registration_status'),
    [yachtData]
  );

  const infoYacht = useMemo(
    () => [
      { icon: NumbersIcon, label: 'ID', value: yachtID },
      {
        icon: Flag,
        label: 'Flag',
        value: _.get(yachtData, 'id_flag.data.attributes.locality_flag'),
      },
      {
        icon: AppRegistrationIcon,
        label: 'Registry',
        value: _.get(yachtData, 'registry_type'),
      },
      { icon: Straighten, label: 'LOA', value: _.get(yachtData, 'loa') },
      {
        icon: IconArrowsHorizontal,
        label: 'Beam',
        value: _.get(yachtData, 'beam'),
      },
      { icon: Height, label: 'Draft', value: _.get(yachtData, 'draft') },
      {
        icon: Scale,
        label: 'Gross Tonn',
        value: _.get(yachtData, 'gross_tonn'),
      },
      {
        icon: DirectionsBoatIcon,
        label: 'Status',
        value: (
          <Chip
            label={status}
            color={color?.color}
            sx={{
              color: color?.color ? '#fff' : color?.textHex,
              backgroundColor: color?.hex,
              opacity: 0.8,
              fontWeight: 500,
            }}
          />
        ),
      },
    ],
    [yachtData, yachtID, color, status]
  );

  const onClickDetails = useCallback(() => {
    navigate(`/yachts/${yachtID}`);
  }, [yachtID, navigate]);

  const onSuccess = useCallback(
    (values) => {
      if (values?.id_new_yacht) {
        const value = {
          id: values?.id_new_yacht?.id,
          name: values?.id_new_yacht?.name,
        };
        methods?.setValue('id_yacht', value);
        saveItem('yachtDashboard', JSON.stringify(value));
      } else {
        saveItem('yachtDashboard', JSON.stringify({}));
        methods?.setValue('id_yacht', null);
      }
    },
    [methods]
  );

  return (
    <Card
      elevation={6}
      sx={{ borderRadius: 4, overflow: 'hidden', width: '100%' }}
    >
      <CardHeader onClick={() => setExpandedCard(!expandedCard)}>
        <DirectionsBoat sx={{ fontSize: 36, color: 'rgba(255,255,255,0.9)' }} />
        <YachtName variant='h6'>{yachtData?.yacht_name}</YachtName>
        <IconButton size='small' sx={{ color: '#fff' }}>
          <ExpandMoreIcon
            sx={{
              transform: expandedCard ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease-in-out',
            }}
          />
        </IconButton>
      </CardHeader>

      <Collapse in={expandedCard} timeout='auto' unmountOnExit>
        <CardContent sx={{ px: 3, pt: 3 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Chip
              label={yachtData?.yacht_type}
              size='small'
              color='primary'
              variant='outlined'
              sx={{ mb: 1, p: 1 }}
            />
          </Box>
          <InfoGrid container spacing={0.5}>
            {infoYacht.map((item) => (
              <InfoItem
                key={item.label}
                icon={item.icon}
                label={item.label}
                value={item.value}
              />
            ))}
          </InfoGrid>
        </CardContent>

        <Divider />

        <Stack
          sx={{ px: 3, py: 2 }}
          direction={{ xs: 'row', lg: 'column', xl: 'row' }}
          justifyContent={'space-between'}
          spacing={2}
        >
          <Box>
            {hasPermissions && (
              <Fragment>
                {_.isEmpty(yachtData?.id_operation_lists?.data) &&
                _.isEmpty(yachtData?.id_booking?.data) &&
                _.isEmpty(yachtData?.id_attendance?.data) &&
                _.isEmpty(yachtData?.id_logbooks?.data) ? (
                  <YachtDeletePage
                    module={{
                      ...yacht,
                      remove: { ...yacht?.remove, navigate: null },
                    }}
                    id={yachtID}
                    variant='contained'
                    onSuccessDelete={onSuccess}
                  />
                ) : (
                  <MoveToTemplate
                    conf={{
                      ...MoveToConf,
                      fields: MoveToConf.fields.map((field) =>
                        field.id === 'id_new_yacht'
                          ? {
                              ...field,
                              api: {
                                ...field?.api,
                                path: `${field?.api?.path}&filters[id][$ne]=${yachtID}`,
                              },
                            }
                          : field
                      ),
                    }}
                    label={
                      <Stack
                        spacing={1}
                        direction={'row'}
                        alignItems={'center'}
                      >
                        <Typography variant='h4' fontWeight={'bold'}>
                          {`Yacht Name: `}
                        </Typography>
                        <Typography
                          variant='h4'
                          fontWeight={'bold'}
                          color={'primary'}
                        >
                          {_.get(yachtData, 'yacht_name')}
                        </Typography>
                        <Chip label={`#${yachtID}`} size='small' />
                      </Stack>
                    }
                    api={API_yachts.moveYacht}
                    body={{ id_old_yacht: yachtID }}
                    onSuccess={onSuccess}
                    disabled={
                      status === STATUS_DRAFT || status === STATUS_ONLINE
                    } //MOVE TO IS NOT POSSIBLE FOR STATUS DRAFT AND ONLINE
                  />
                )}
              </Fragment>
            )}
          </Box>

          <Button
            variant='contained'
            onClick={onClickDetails}
            sx={{ width: 113 }}
          >
            View Details
          </Button>
        </Stack>
      </Collapse>
    </Card>
  );
};

export default YachtCard;
